import {
    type PriceCalculationParameterInfo,
    type CalculationFormula,
    type PriceCalculationFormula,
    type PriceCalculationUnit,
} from "../api";
import { type ServiceParameterGroupEntity } from "../configuration";
import {
    type ParsedUnit,
    parseUnitComponents,
    findUnitCombinationsRecursive,
    invertParsedUnit,
    buildUnitString,
} from "./ParsedUnit";

/**
 * Internal wrapper type for parameters that tracks the effective unit
 * (which may be inverted) and whether the parameter is used inversely.
 */
type InternalParameterInfo = {
    param: PriceCalculationParameterInfo;
    effectiveUnit: PriceCalculationUnit; // the unit used for matching (inverted if isInverse)
    isInverse: boolean;
};

/**
 * Generates cartesian product of parameter arrays.
 */
const cartesianProduct = (arrays: InternalParameterInfo[][]): InternalParameterInfo[][] => {
    if (arrays.length === 0) {
        return [[]];
    }
    if (arrays.length === 1) {
        return arrays[0]!.map(p => [p]);
    }

    const [first, ...rest] = arrays;
    const restProduct = cartesianProduct(rest);

    const result: InternalParameterInfo[][] = [];
    for (const param of first!) {
        for (const combination of restProduct) {
            result.push([param, ...combination]);
        }
    }
    return result;
};

/**
 * Checks if a combination is valid for restricted parameters.
 * Valid if: at least one param has combineWithParameters AND all restrictions are satisfied.
 */
const isValidRestrictedCombination = (combination: InternalParameterInfo[]): boolean => {
    // Must have at least one parameter with restrictions
    const hasRestricted = combination.some(info => info.param.combineWithParameters !== undefined);
    if (!hasRestricted) {
        return false;
    }

    // All parameters with restrictions must have their conditions fulfilled
    for (const info of combination) {
        if (info.param.combineWithParameters !== undefined) {
            const hasAllowedPartner = combination.some(
                other =>
                    other.param.id !== info.param.id &&
                    info.param.combineWithParameters!.includes(other.param.id)
            );
            if (!hasAllowedPartner) {
                return false;
            }
        }
    }

    return true;
};

/**
 * Generates all valid restricted parameter combinations for a unit chain.
 */
const generateRestrictedCombinations = (
    unitChain: PriceCalculationUnit[],
    paramsByUnit: Map<PriceCalculationUnit, InternalParameterInfo[]>
): InternalParameterInfo[][] => {
    // Get parameter arrays for each unit in the chain
    const paramArrays = unitChain.map(unit => paramsByUnit.get(unit)!);

    // Generate cartesian product
    const allCombinations = cartesianProduct(paramArrays);

    // Filter to valid restricted combinations
    return allCombinations.filter(isValidRestrictedCombination);
};

/**
 * Find all combinations of parameters whose units multiply to the target unit.
 * Uses an optimized recursive algorithm that works at the unit level.
 */
const findParameterCombinations = (
    targetUnit: PriceCalculationUnit,
    availableParameters: InternalParameterInfo[]
): InternalParameterInfo[][] => {
    // Step 1: Group parameters by effectiveUnit (not param.unit)
    const paramsByUnit = new Map<PriceCalculationUnit, InternalParameterInfo[]>();
    for (const info of availableParameters) {
        if (!paramsByUnit.has(info.effectiveUnit)) {
            paramsByUnit.set(info.effectiveUnit, []);
        }
        paramsByUnit.get(info.effectiveUnit)!.push(info);
    }

    // Build available units - include duplicates for same-unit multiplication (e.g., m × m = m²)
    // But only for non-target units to avoid duplicate direct matches
    const availableUnits: PriceCalculationUnit[] = [];
    for (const [unit, params] of paramsByUnit) {
        if (unit === targetUnit) {
            // For target unit, only add once (direct matches handled separately)
            availableUnits.push(unit);
        } else {
            // For non-target units, add once per parameter to allow same-unit multiplication
            for (let i = 0; i < params.length; i++) {
                availableUnits.push(unit);
            }
        }
    }

    // Step 2: Pre-parse all units once
    const parsedUnitsMap = new Map<PriceCalculationUnit, ParsedUnit>();
    availableUnits.forEach(unit => {
        parsedUnitsMap.set(unit, parseUnitComponents(unit));
    });
    const targetParsed = parseUnitComponents(targetUnit);
    const scalarParsed: ParsedUnit = { nums: [], denoms: [] };

    // Step 3: Find combinations of UNITS (using parsed structures)
    const unitChains = findUnitCombinationsRecursive(
        targetParsed,
        scalarParsed,
        [],
        availableUnits,
        parsedUnitsMap
    );

    // Step 4: Convert unit chains to parameter combinations
    const results: InternalParameterInfo[][] = [];
    const processedSignatures = new Set<string>();

    for (const unitChain of unitChains) {
        // Create signature from sorted units to avoid duplicates from multiple unit instances
        const unitSignature = [...unitChain].sort().join(" * ");
        if (processedSignatures.has(unitSignature)) {
            continue;
        }
        processedSignatures.add(unitSignature);

        // For this unit chain, collect unrestricted parameters per unit
        const unrestrictedByUnit: InternalParameterInfo[][] = [];
        let hasCombineWithParameters = false;

        for (const unit of unitChain) {
            const paramsForUnit = paramsByUnit.get(unit)!;
            const unrestrictedForUnit = paramsForUnit.filter(
                info => info.param.combineWithParameters === undefined
            );

            if (paramsForUnit.some(info => info.param.combineWithParameters !== undefined)) {
                hasCombineWithParameters = true;
            }

            unrestrictedByUnit.push(unrestrictedForUnit);
        }

        // Add restricted combinations first, then unrestricted
        if (hasCombineWithParameters) {
            const restrictedCombinations = generateRestrictedCombinations(unitChain, paramsByUnit);
            results.push(...restrictedCombinations);
        }

        // Only add unrestricted combination if ALL units have at least one unrestricted parameter
        if (unrestrictedByUnit.every(arr => arr.length > 0)) {
            const paramCombination = unrestrictedByUnit.flat();
            results.push(paramCombination);
        }
    }

    return results;
};

export const createPriceCalculation = (
    targetUnit: PriceCalculationUnit,
    parameterGroups?: ServiceParameterGroupEntity[]
): PriceCalculationFormula | undefined => {
    const calculationParameters: PriceCalculationParameterInfo[] =
        parameterGroups
            ?.flatMap(group => group.getParameters())
            .flatMap(parameter => parameter.getParameterValue().getCalculationParameterParameterInfos()) ??
        [];
    if (calculationParameters.length === 0) {
        return undefined;
    }

    const scalarParameters = calculationParameters.filter(parameter => parameter.unit === "scalar");

    // Find parameters that could be combined (exclude only scalars)
    const combinableParameters = calculationParameters.filter(parameter => parameter.unit !== "scalar");

    // Build InternalParameterInfo[] with both original and inverted versions
    const internalParams: InternalParameterInfo[] = [];
    for (const param of combinableParameters) {
        const parsedUnit = parseUnitComponents(param.unit);
        const normalizedUnit = buildUnitString(parsedUnit);

        // Add original parameter (with normalized unit)
        internalParams.push({
            param,
            effectiveUnit: normalizedUnit,
            isInverse: false,
        });

        // Add inverted version for all non-scalar parameters
        // (scalar inverted is still scalar, so no point adding it)
        if (param.unit !== "scalar") {
            const invertedUnit = buildUnitString(invertParsedUnit(parsedUnit));
            // Only add if inverted unit is different from original
            if (invertedUnit !== normalizedUnit) {
                internalParams.push({
                    param,
                    effectiveUnit: invertedUnit,
                    isInverse: true,
                });
            }
        }
    }

    // Find all combinations that multiply to target unit
    const combinations = findParameterCombinations(targetUnit, internalParams);

    // Generate calculation entries for each combination
    const targetUnitParametersFromMultiplication: CalculationFormula[] = [];

    for (const combination of combinations) {
        if (combination.length === 0) {
            continue;
        }

        // Group parameters by their effectiveUnit for this single combination
        const parametersByUnit = new Map<PriceCalculationUnit, InternalParameterInfo[]>();
        const unitFirstIndex = new Map<PriceCalculationUnit, number>();

        for (const info of combination) {
            if (!parametersByUnit.has(info.effectiveUnit)) {
                parametersByUnit.set(info.effectiveUnit, []);
                // Track the index of this parameter in the original list
                const originalIndex = combinableParameters.findIndex(p => p.id === info.param.id);
                unitFirstIndex.set(info.effectiveUnit, originalIndex);
            }
            parametersByUnit.get(info.effectiveUnit)!.push(info);
        }

        // Sort units based on the pattern:
        // - For simple rate*quantity (2 units, one starts with target), put non-target first
        // - For chains (3+ units) or other patterns, preserve original order
        const unitKeys = Array.from(parametersByUnit.keys());
        const targetPrefixedCount = unitKeys.filter(u => u.startsWith(targetUnit + "/")).length;

        const sortedUnits = unitKeys.sort((a, b) => {
            // Only apply special sorting for 2-unit products with exactly one target-prefixed
            if (unitKeys.length === 2 && targetPrefixedCount === 1) {
                const aStartsWithTarget = a.startsWith(targetUnit + "/");
                const bStartsWithTarget = b.startsWith(targetUnit + "/");

                if (aStartsWithTarget && !bStartsWithTarget) {
                    return 1; // b comes first
                }
                if (!aStartsWithTarget && bStartsWithTarget) {
                    return -1; // a comes first
                }
            }

            // Otherwise, maintain original order
            return (unitFirstIndex.get(a) ?? 0) - (unitFirstIndex.get(b) ?? 0);
        });

        // Collect min/max constraints from all parameters
        const minValues: number[] = [];
        const maxValues: number[] = [];

        for (const infos of parametersByUnit.values()) {
            infos.forEach(info => {
                if (info.param.productMin !== undefined) {
                    minValues.push(info.param.productMin);
                }
                if (info.param.productMax !== undefined) {
                    maxValues.push(info.param.productMax);
                }
            });
        }

        const productMin = minValues.length > 0 ? Math.max(...minValues) : undefined;
        const productMax = maxValues.length > 0 ? Math.min(...maxValues) : undefined;

        // Special case: single-unit chains (params already at target unit)
        // Each parameter should be a separate entry, not grouped
        if (sortedUnits.length === 1 && sortedUnits[0] === targetUnit) {
            const infos = parametersByUnit.get(targetUnit)!;
            // Sort parameters by their original order
            const sortedInfos = infos.sort((a, b) => {
                const aIndex = combinableParameters.findIndex(p => p.id === a.param.id);
                const bIndex = combinableParameters.findIndex(p => p.id === b.param.id);
                return aIndex - bIndex;
            });

            // Add each parameter as a separate entry
            for (const info of sortedInfos) {
                targetUnitParametersFromMultiplication.push(createCalculationEntry(info, scalarParameters));
            }
            continue;
        }

        // Build PROD entries - if multiple params have same unit, wrap in SUM
        const prodEntries: CalculationFormula[] = [];
        for (const unit of sortedUnits) {
            const infos = parametersByUnit.get(unit)!;
            // Sort parameters by their original order
            const sortedInfos = infos.sort((a, b) => {
                const aIndex = combinableParameters.findIndex(p => p.id === a.param.id);
                const bIndex = combinableParameters.findIndex(p => p.id === b.param.id);
                return aIndex - bIndex;
            });

            if (sortedInfos.length === 1) {
                prodEntries.push(createCalculationEntry(sortedInfos[0]!, scalarParameters));
            } else {
                // Multiple parameters with same unit - create SUM
                prodEntries.push({
                    type: "SUM",
                    entries: sortedInfos.map(info => createCalculationEntry(info, scalarParameters)),
                });
            }
        }

        // Create PROD formula (or unwrap if only one entry)
        let calculationEntry: CalculationFormula;
        if (prodEntries.length === 1) {
            calculationEntry = prodEntries[0]!;
        } else {
            calculationEntry = {
                type: "PROD",
                entries: prodEntries,
            };
        }

        // Apply min/max constraints
        if (productMin) {
            calculationEntry = {
                type: "MAX",
                entries: [
                    calculationEntry,
                    { type: "CONSTANT", value: { value: productMin, unit: targetUnit } },
                ],
            };
        }
        if (productMax) {
            calculationEntry = {
                type: "MIN",
                entries: [
                    calculationEntry,
                    { type: "CONSTANT", value: { value: productMax, unit: targetUnit } },
                ],
            };
        }

        targetUnitParametersFromMultiplication.push(calculationEntry);
    }

    const filteredMultiplications = targetUnitParametersFromMultiplication;

    if (filteredMultiplications.length > 0) {
        let result: CalculationFormula = {
            type: "SUM",
            entries: filteredMultiplications,
        };

        const scalarParameterWithoutReferences = scalarParameters?.filter(
            parameter => parameter.combineWithParameters === undefined
        );
        if (scalarParameterWithoutReferences?.length > 0) {
            result = {
                type: "PROD",
                entries: [
                    ...scalarParameterWithoutReferences.map(parameter =>
                        createCalculationEntry(parameter, undefined)
                    ),
                    result,
                ],
            };
        }
        return result;
    }
    return undefined;
};

const createCalculationEntry = (
    input: InternalParameterInfo | PriceCalculationParameterInfo,
    scalarParameters?: PriceCalculationParameterInfo[]
): CalculationFormula => {
    // Extract param and isInverse from input
    const isInternalInfo = "isInverse" in input;
    const param = isInternalInfo ? input.param : input;
    const isInverse = isInternalInfo ? input.isInverse : false;

    let matchingCalculationEntry: CalculationFormula = {
        type: "REFERENCE",
        reference: param.id,
    };

    // Wrap in INV if this parameter is used inversely
    if (isInverse) {
        matchingCalculationEntry = {
            type: "INV",
            entry: matchingCalculationEntry,
        };
    }
    /*    if (p.getParameterValue().getKind() === "lookup") {
        matchingCalculationEntry = {
            type: "LOOKUP",
            reference: p.reference,
            unit: p.unit,
            defaultValue: p.defaultValue,
            tiers: p.tiers,
        };
    }
    if (p.kind === "list") {
        matchingCalculationEntry = {
            type: "LIST",
            reference: p.key,
            unit: p.unit,
            calculation: this.createPriceCalculationModel(p.unit, [
                {
                    id: "list-parameter",
                    name: "Liste",
                    parameters: this.parameterGroups
                        .flatMap(group => group.parameters)
                        .filter(parameter => parameter.kind !== "list")
                        .filter(parameter => parameter.unit !== "scalar" || parameter.combineWithParameters),
                },
                {
                    id: p.key,
                    name: p.name,
                    parameters: p.entries,
                },
            ]) as CalculationEntry,
        };
    }
*/
    const matchingScalarParameters = scalarParameters?.filter(sp =>
        sp.combineWithParameters?.includes(param.id)
    );
    if (matchingScalarParameters && matchingScalarParameters.length > 0) {
        matchingCalculationEntry = {
            type: "PROD",
            entries: [
                matchingCalculationEntry,
                ...matchingScalarParameters.map(sp => createCalculationEntry(sp, undefined)),
            ] as CalculationFormula[],
        };
    }
    return matchingCalculationEntry;
};
